import { Spinner } from '@vmware-react/clarity/dist/spinner';
import cn from 'classnames';
import React, { useEffect, useState } from 'react';
import './Steps.css';
import { StepModel, StepOrientation, StepState } from './types';

interface StepsProps {
  orientation?: StepOrientation; // defaults to horizontal
  bufferSize?: number;
  steps: StepModel[];
  currentStep: number; // 0 indexed
  onClick?: (index: number) => void;
}

interface StepProps {
  first: boolean;
  last: boolean;
  children: React.ReactNode;
  active: boolean;
  disabled: boolean;
  orientation: StepOrientation;
  index: number;
  totalSteps?: number;
  title?: string;
  onClick?: (index: number) => void;
}

interface ContentProps {
  state: StepState;
  title: string;
}

interface BufferState {
  start?: number;
  end?: number;
}

export const Steps: React.FC<StepsProps> = props => {
  const {
    bufferSize,
    currentStep,
    onClick,
    orientation = 'horizontal',
    steps
  } = props;
  const [buffer, setBuffer] = useState<BufferState>({});

  useEffect(() => {
    // step count changed and bufferEnd is beyond last step.
    if (bufferSize && buffer.end && buffer.end > steps.length - 1) {
      setBuffer({
        end: steps.length - 1,
        start: Math.max(0, buffer.end - bufferSize + 1)
      });
    }
  }, [steps.length, buffer.end, bufferSize]);

  useEffect(() => {
    // current step changed or buffer value changed
    // check if the current step lies within buffer start and buffer end.
    // as the step change can be arbitrary, we will find the buffer
    // which should contain the current active step
    if (buffer.end && buffer.start && bufferSize) {
      if (
        buffer.end - buffer.start + 1 !== bufferSize ||
        currentStep > buffer.end ||
        currentStep < buffer.start
      ) {
        setBuffer({
          start: (Math.ceil((currentStep + 1) / bufferSize) - 1) * bufferSize,
          end: Math.min(buffer.start + bufferSize - 1, steps.length - 1)
        });
      }
    }
  }, [currentStep, steps.length, buffer.start, buffer.end, bufferSize]);

  useEffect(() => {
    // buffer is just enabled.
    if (bufferSize) {
      setBuffer({ start: 0, end: bufferSize - 1 });
    }
  }, [bufferSize]);

  const classNames = cn('crc-step', `crc-step-${orientation}`);

  const onStepClick = (index: number) => {
    if (onClick) {
      onClick(index);
    }
  };

  const onBufferPrevious = () => {
    if (bufferSize && buffer.end && buffer.start) {
      const newStart = Math.max(0, buffer.start - bufferSize);
      setBuffer({
        start: newStart,
        end: Math.min(steps.length - 1, newStart + bufferSize - 1)
      });
    }
  };

  const onKeyUpPrev = (e: React.KeyboardEvent) =>
    (e.keyCode === 13 || e.keyCode === 32) && onBufferPrevious();

  const onBufferNext = () => {
    if (bufferSize && buffer.end && buffer.start) {
      const newEnd = Math.min(steps.length - 1, buffer.end + bufferSize);
      setBuffer({ start: Math.max(0, newEnd - bufferSize + 1), end: newEnd });
    }
  };

  const onKeyUpNext = (e: React.KeyboardEvent) =>
    (e.keyCode === 13 || e.keyCode === 32) && onBufferNext();

  const renderPreviousIcon = () => {
    if (
      orientation === 'horizontal' &&
      bufferSize &&
      buffer.start &&
      buffer.start !== 0
    ) {
      return (
        <div
          className="crc-step-buffer-icon"
          onClick={onBufferPrevious}
          role="button"
          tabIndex={0}
          onKeyUp={onKeyUpPrev}>
          <clr-icon shape="angle-double left" size={32} />
        </div>
      );
    }
  };

  const renderNextIcon = () => {
    if (
      orientation === 'horizontal' &&
      bufferSize &&
      buffer.end &&
      buffer.end !== steps.length - 1
    ) {
      return (
        <div
          className="crc-step-buffer-icon"
          onClick={onBufferNext}
          role="button"
          tabIndex={0}
          onKeyUp={onKeyUpNext}>
          <clr-icon shape="angle-double right" size={32} />
        </div>
      );
    }
  };

  return (
    <div className={classNames}>
      {renderPreviousIcon()}
      <div className="crc-step-renderer-wrapper">
        {steps.map(
          (step, index) =>
            (orientation === 'horizontal' &&
            bufferSize &&
            buffer.start &&
            buffer.end
              ? index >= buffer.start && index <= buffer.end
              : true) && (
              <Step
                key={index}
                index={index}
                first={bufferSize ? index === buffer.start : index === 0}
                active={currentStep === index}
                last={
                  bufferSize ? index === buffer.end : index === steps.length - 1
                }
                totalSteps={steps.length}
                title={step.title}
                disabled={step.disabled}
                onClick={onStepClick}
                orientation={orientation}>
                <StepContent title={step.title} state={step.state} />
              </Step>
            )
        )}
      </div>
      {renderNextIcon()}
    </div>
  );
};

const Step: React.FC<StepProps> = props => {
  const {
    active,
    children,
    disabled,
    first,
    index,
    last,
    totalSteps,
    title,
    onClick,
    orientation
  } = props;

  const classNames = cn('crc-step-item', `crc-step-item-${orientation}`, {
    'crc-step-item-first': first,
    'crc-step-item-last': last,
    'crc-step-item-active': active,
    'crc-step-item-disabled': disabled
  });

  const handleClick = () => {
    if (onClick) {
      onClick(index);
    }
  };

  const handleKeyUp = (e: React.KeyboardEvent) =>
    (e.keyCode === 13 || e.keyCode === 32) && onClick && onClick(index);

  return (
    <div className={classNames}>
      {orientation === 'horizontal' ? (
        <div
          className={cn('shape-btn-wrapper', {
            'pointer-events-none': disabled
          })}
          onClick={handleClick}>
          <div
            className="shape-wrapper"
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
            aria-label={`${!disabled && 'current'} step ${
              index + 1
            } of ${totalSteps}. ${title}`}
            onKeyUp={handleKeyUp}>
            <div className="crc-step-item-top-p-gram" />
            <div className="crc-step-item-bottom-p-gram" />
            {first && <div className="cover-left-gap" />}
            {last && <div className="cover-right-gap" />}
          </div>
          <div aria-hidden="true" className="crc-step-item-content">
            {children}
          </div>
        </div>
      ) : (
        <div
          className={cn('crc-step-item-content-wrapper', {
            'pointer-events-none': disabled
          })}
          onClick={handleClick}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-disabled={disabled}
          aria-label={`${!disabled && 'current'} step ${
            index + 1
          } of ${totalSteps}. ${title}`}
          onKeyUp={handleKeyUp}>
          <div aria-hidden="true" className="crc-step-item-content">
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

const StepContent: React.FC<ContentProps> = props => {
  const { state, title } = props;
  return (
    <div className="crc-step-content-renderer-wrapper">
      <div className="crc-step-content-renderer-icon-wrapper">
        {state === 'complete' && (
          <clr-icon shape="success-standard" class="is-solid is-success" />
        )}
        {state === 'warning' && (
          <clr-icon shape="success-standard" class="is-solid is-warning" />
        )}
        {state === 'danger' && (
          <clr-icon shape="error-standard" class="is-solid is-error" />
        )}
        {state === 'loading' && <Spinner size="sm" />}
      </div>
      <div className="crc-step-content-renderer-title" title={title}>
        {title}
      </div>
      <div className="crc-step-content-renderer-icon-wrapper" />
    </div>
  );
};
